//			impactArray = new Array("No Impact","Low Impact", "Medium Impact", "High Impact", "Closed"),
//this may not be right
export const impactMapping: { [key: number]: string } = {
  0: 'None',
  1: 'Low',
  2: 'Medium',
  3: 'High',
  4: 'Closed',
};

export const getImpactLevel = (impact: number): string => {
  return impactMapping[impact] || 'Unknown';
};

export const eventImpactSort = (a: any, b: any): number => {
  // Sort by impact level (higher impact first)
  const aImpact = ((a as Record<string, unknown>)?.impact as number) ?? 0;
  const bImpact = ((b as Record<string, unknown>)?.impact as number) ?? 0;

  // If impact levels are different, sort by impact descending
  if (aImpact !== bImpact) {
    return bImpact - aImpact;
  }

  // If impact levels are the same, sort by lastUpdated descending (most recent first)
  const aUpdated = getDateFromString(
    (a as Record<string, unknown>)?.lastEventUpdate as string
  );
  const bUpdated = getDateFromString(
    (b as Record<string, unknown>)?.lastEventUpdate as string
  );
  return bUpdated - aUpdated;
};

export const getDateFromString = (dateString: string | undefined): number => {
  if (!dateString) return 0;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 0;
    }
    return date.getTime();
  } catch {
    return 0;
  }
};

export const getAdvisoryText = (advisoryText: string) => {
  return decodeURIComponent(advisoryText).replace(/&amp;/g, '&');
};

export const getLocationDescription = (icon: any) => {
  if (icon.offsetLocation && icon.offsetLocation.overrideLocation) {
    return icon.offsetLocation.overrideLocation;
  }
  if (icon.location.overrideLocation) return icon.location.overrideLocation;
  if (icon.type.toLowerCase() === 'traveltimes' && '' !== icon.name) {
    return icon.name;
  }
  if (
    icon.type.toLowerCase() === 'planned' ||
    icon.type.toLowerCase() === 'traveltimes'
  ) {
    const startXStreet = icon.location.xstreet;
    let description = icon.location.route;
    if (
      icon.endLocation &&
      '' !== icon.endLocation.xstreet &&
      startXStreet !== icon.endLocation.xstreet
    ) {
      description =
        description +
        ' from ' +
        startXStreet +
        ' to ' +
        icon.endLocation.xstreet;
    } else {
      description = description + ' at ' + startXStreet;
    }
    return description;
  }

  if (icon.type.toLowerCase() === 'snowplow') {
    return icon.type + ' #' + icon.vehicleId;
  }

  if (icon.location.route !== '' && icon.location.xstreet !== '') {
    return icon.location.route + ' @ ' + icon.location.xstreet;
  }
  if (icon.location.route !== '') {
    return `${icon.location.route}`;
  }
};

export const getJobDescription = (icon: any) => {
  let description = '';
  if (undefined !== icon.jobdetails && '' !== icon.jobdetails.description) {
    description = icon.jobdetails.description;
  }
  return description;
};

export const checkJobDetailsUrl = (iconOrUrl: any) => {
  if (typeof iconOrUrl === 'string') {
    if (iconOrUrl === '') return;
    if (iconOrUrl.indexOf('http') < 0) {
      return 'https://' + iconOrUrl;
    } else return iconOrUrl;
  }
  return '';
};

export const getUpdated = (
  icon: any,
  maxAge: string | undefined,
  fieldName = 'lastRpUpdate'
) => {
  const maxAgeInMin = parseInt(maxAge ?? '0', 0) || 0;
  const updateField = icon[fieldName] ?? icon.lastRpUpdate;
  if (0 === maxAgeInMin) return `Updated ${timeAgoString(updateField)}`;
  const updateExpires = new Date(updateField);
  updateExpires.setMinutes(updateExpires.getMinutes() + maxAgeInMin);
  const now = new Date();
  if (now < updateExpires) {
    return `Updated ${timeAgoString(updateField)}`;
  } else {
    if (timeAgoString(updateField) === 'Unknown') {
      return ``;
    }
    return `Last updated over ${formatMaxAge(maxAgeInMin)} ago`;
  }
};

const formatMaxAge = (maxAgeInMin: number): string => {
  if (maxAgeInMin < 60) {
    return maxAgeInMin === 1 ? '1 minute' : `${maxAgeInMin} minutes`;
  }
  if (maxAgeInMin < 1440) {
    // Less than a day
    const hours = Math.floor(maxAgeInMin / 60);
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  if (maxAgeInMin < 10080) {
    // Less than a week (7 days)
    const days = Math.floor(maxAgeInMin / 1440);
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (maxAgeInMin < 43200) {
    // Less than a month (30 days)
    const weeks = Math.floor(maxAgeInMin / 10080);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
  const months = Math.floor(maxAgeInMin / 43200);
  return months === 1 ? '1 month' : `${months} months`;
};

export const timeAgoString = (date: number): string => {
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return 'Unknown';
  }
  const now = new Date();
  const diff = Math.abs(now.getTime() - dateObj.getTime());
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) {
    return 'less than a minute ago';
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    if (minutes === 1) {
      return '1 minute ago';
    }
    return `${minutes} minutes ago`;
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    if (hours === 1) {
      return '1 hour ago';
    }
    return `${hours} hours ago`;
  }
  if (seconds < 604800) {
    const days = Math.floor(seconds / 86400);
    if (days === 1) {
      return '1 day ago';
    }
    return `${days} days ago`;
  }
  if (seconds < 2419200) {
    const weeks = Math.floor(seconds / 604800);
    if (weeks === 1) {
      return '1 week ago';
    }
    return `${weeks} weeks ago`;
  }
  if (seconds < 29030400) {
    const months = Math.floor(seconds / 2419200);
    if (months === 1) {
      return '1 month ago';
    }
    return `${months} months ago`;
  }
  if (seconds >= 29030400) {
    const years = Math.floor(seconds / 29030400);
    if (years === 1) {
      return '1 year ago';
    }
    return `${years} years ago`;
  }
  return 'Unknown';
};

export const getAgencyName = (icon: any) => {
  if (icon.jobdetails && icon.jobdetails.contactagency) {
    return icon.jobdetails.contactagency;
  }
  if (icon.agency) {
    return icon.agency ?? icon.agency.name ?? icon.agency.name;
  }
  return 'Unknown Agency';
};

export const getNormalizedIcon = (icon: any) => {
  return icon?.id ? icon : icon?.icon; // If it has `id`, it's already normalized
};

export const getEventSubtype = (icon: any) => {
  if (icon.type.toLowerCase() === 'planned') {
    if (icon.scheduled && icon.schedules.length > 0) {
      if (icon.jobdetails && icon.jobdetails.description) {
        return `${icon.jobdetails.description} (Planned)`;
      } else return 'Planned';
    }
    return icon.jobdetails.description || 'Roadwork';
  } else {
    if (icon.subType.toLowerCase() === 'accident') {
      return 'Crash';
    }
    return icon.subType || 'Unknown';
  }
};

export const isRoadwork = (icon: any) => {
  return icon.type.toLowerCase() === 'planned';
};

export const getRoadworkIconString = (icon: any): string => {
  const normalizedIcon = getNormalizedIcon(icon);
  return `/mapicons/${normalizedIcon.icon.replace('png', 'svg')}`;
};

export const getRoadworkIcon = (icon: any) => {
  const normalizedIcon = getNormalizedIcon(icon);
  const selectedIcon = getRoadworkIconString(normalizedIcon);
  let name = 'Active Roadwork';

  const typeText = normalizedIcon.icon?.toLowerCase() || '';

  if (
    typeText.includes('active') &&
    typeText.includes('schedule') &&
    typeText.includes('closed')
  ) {
    name = 'Road Closed';
  } else if (typeText.includes('active') && typeText.includes('schedule')) {
    name = 'Active Roadwork';
  } else if (typeText.includes('schedule') && typeText.includes('closed')) {
    name = 'Planned Closure';
  } else if (typeText.includes('schedule')) {
    name = 'Planned Roadwork';
  }

  if (normalizedIcon.open) {
    name = 'Road Open';
  }

  return { src: selectedIcon, name: name, alt: name };
};

const signalFlash = 'Signal Flash';
const signalOut = 'Signal Out';

export const getIncidentIconString = (icon: any): string => {
  const normalizedIcon = getNormalizedIcon(icon);
  if (!normalizedIcon || !normalizedIcon.icon) {
    // fallback icon
    return '/mapicons/incident.svg';
  }
  return `/mapicons/${normalizedIcon.icon.replace('png', 'svg')}`;
};

export const getIncidentIcon = (icon: any) => {
  const normalizedIcon = getNormalizedIcon(icon);
  let selectedIcon = getIncidentIconString(normalizedIcon);
  let name = 'Active Incident';

  const subType = normalizedIcon?.subType || '';

  if (subType === signalFlash) {
    name = signalFlash;
    selectedIcon = '/mapicons/signal-flash.svg';
  } else if (subType === signalOut) {
    name = signalOut;
    selectedIcon = '/mapicons/signal-out.svg';
  }

  const typeText = normalizedIcon.icon?.toLowerCase() || '';

  if (
    typeText.includes('active') &&
    typeText.includes('schedule') &&
    typeText.includes('closed')
  ) {
    name = 'Road Closed';
  } else if (typeText.includes('active') && typeText.includes('schedule')) {
    name = 'Active Incident';
  } else if (typeText.includes('schedule') && typeText.includes('closed')) {
    name = 'Planned Closure';
  } else if (typeText.includes('schedule')) {
    name = 'Planned Incident';
  }

  if (normalizedIcon.open) {
    name = 'Road Open';
  }

  return { src: selectedIcon, name: name, alt: name };
};
